import { useState, useContext, useEffect } from 'react'
import jsLogger from 'js-logger'

import { conversationStart, sendSessionStarted } from 'controllers/main'
// import { getBackClient, BackClient } from 'controllers/back'
import { BackgroundType, SessionType } from 'types/internal'
import LocalizationContext from 'shared/contexts/LocalizationContext'
import ConnectionContextDataProvider from 'shared/contexts/ConnectionContextDataProvider'
import { StageT, StageType } from 'src/localTypes'
import { Proto } from 'types/demo3'
import has from 'lodash/has'
import Initialization from 'pages/Initialization'
import PermissionsFlow from 'pages/PermissionsFlow'
import Session from 'pages/Session'
import { getBackClient } from 'shared/controllers/back'
import ErrorPage from 'pages/ErrorPage'
import FixPermission from 'shared/components/FixPermission'
import SessionReport from 'pages/SessionReport'
import Registration from 'pages/Registration'
import PostRegistration from 'pages/PostRegistration'
import { PermissionErrorType } from 'shared/contexts/StreamDataProvider'
import IdleTimeout from 'pages/IdleTimeout'
import { SessionInfo } from 'types/back'
import SessionVideoRecorderDataProvider from 'shared/contexts/SessionVideoRecorderDataProvider'

// const SESSION_TIMEOUT = 600000

const App = () => {
  const { setLocalization } = useContext(LocalizationContext)
  const [sessionInfo, setSessionInfo] = useState<
    Proto.ConversationStartResponse | undefined
  >()

  const [stage, setStage] = useState<StageT>({
    type: StageType.INITIALIZATION
  })

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const recapSessionId = urlParams.get('recap')
    if (recapSessionId) {
      setStage({
        type: StageType.RECAP,
        sessionId: recapSessionId,
        animationOn: false
      })
    } else {
      startSession()
      setStage({ type: StageType.INITIALIZATION })
    }
  }, [])

  const startSession = async () => {
    const url = new URL(window.location.href)
    const back = getBackClient()
    const urlLang = url.searchParams.get('lang')
    jsLogger.log('conversationStart api call start')
    const convStartTime = Date.now()
    const apires = await conversationStart(urlLang)
    jsLogger.debug('conversation started', {
      latency: Date.now() - convStartTime
    })
    if (has(apires, 'sessionId')) {
      const r = apires as Proto.ConversationStartResponse
      setSessionInfo(r)
      const isLocalhost =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      const sessionInfo: SessionInfo = {
        sessionId: r.sessionId,
        sessionType: SessionType.DEMO3,
        isLocalhost
      }
      back.start(sessionInfo)
      setLocalization(r.sessionSettings.localization)
      setStage({ type: StageType.REGISTRATION })
    } else {
      const r = apires as Proto.ErrorResponse
      setStage({ type: StageType.ERROR, error: r.message })
    }
  }

  // const onChangeLang = (l: string) => {
  //   jsLogger.debug('onChangeLang', { lang: l })
  //   if (interactionId) {
  //     interactionSetLang(interactionId, l)
  //     setLang(l)
  //   }
  // }

  const onError = (er: string) => {
    jsLogger.error('on error: ', er)
    setStage({ type: StageType.ERROR, error: er })
  }

  const handlePermissionsError = (errorType: PermissionErrorType) => {
    jsLogger.error('Session: Permissions error from PermissionsFlow', errorType)
    setStage({ type: StageType.PERMISSIONS_ERROR, errorType })
  }

  const handlePermissionsGranted = () => {
    setStage({ type: StageType.STARTED })
  }

  const onRegisterationComplete = (
    name: string,
    mediaType: string,
    photo: Blob
  ) => {
    console.log('onRegisterationComplete', name, mediaType, photo.size)
    if (sessionInfo) {
      setStage({ type: StageType.POST_REGISTRATION, name, mediaType, photo })
    }
  }

  const onComplete = () => {
    jsLogger.log('App: onComplete start')
    if (sessionInfo) {
      jsLogger.log('conversation finished')
      const url = new URL(window.location.href)
      jsLogger.log('App: onComplete: adding recap to url query params')
      url.searchParams.set('recap', sessionInfo.sessionId)
      if (window.history?.replaceState) {
        window.history.replaceState(window.history.state, '', url)
      }
      getBackClient().disconnect()
      setTimeout(() => {
        setStage({
          type: StageType.RECAP,
          sessionId: sessionInfo.sessionId,
          animationOn: true
        })
        // window.location.href = url.toString()
      }, 500) // Add 1 second timeout before reloading
    }
  }

  const onVerify = () => {
    if (sessionInfo) {
      sendSessionStarted(sessionInfo?.sessionId)
      setStage({ type: StageType.STARTED })
    }
  }

  const onSessionIdleTimeout = () => {
    jsLogger.log('on session idle timeout')
    setStage({ type: StageType.IDLE_TIMEOUT })
  }

  const renderContent = () => {
    switch (stage.type) {
      case StageType.IDLE_TIMEOUT: {
        return (
          <IdleTimeout locale={sessionInfo?.sessionSettings.lang || 'en'} />
        )
      }
      case StageType.REGISTRATION: {
        if (sessionInfo) {
          return (
            <Registration
              onComplete={onRegisterationComplete}
              sessionId={sessionInfo.sessionId}
              locale={sessionInfo?.sessionSettings.lang}
            />
          )
        } else {
          return <Initialization />
        }
      }
      case StageType.POST_REGISTRATION: {
        return (
          <PostRegistration
            name={stage.name}
            mediaType={stage.mediaType}
            photo={stage.photo}
            onRegister={() => setStage({ type: StageType.REGISTRATION })}
            onVerify={onVerify}
            locale={sessionInfo?.sessionSettings.lang || 'en'}
          />
        )
      }
      case StageType.INITIALIZATION: {
        return <Initialization />
      }
      case StageType.PERMISSIONS_REQUEST: {
        return (
          <PermissionsFlow
            lang={sessionInfo?.sessionSettings.lang}
            onPermissionsGranted={handlePermissionsGranted}
            onPermissionsError={handlePermissionsError}
            customization={{
              backgroundType: BackgroundType.AVATAR
            }}
            avatarImageUrl={sessionInfo?.sessionSettings?.avatarImageUrl}
          />
        )
      }
      case StageType.RECAP: {
        return (
          <SessionReport
            sessionId={stage.sessionId}
            animationOn={stage.animationOn}
          />
        )
      }
      case StageType.ERROR: {
        return (
          <ErrorPage
            error={stage.error}
            locale={sessionInfo?.sessionSettings.lang || 'en'}
          />
        )
      }
      case StageType.PERMISSIONS_ERROR: {
        if (stage.errorType === PermissionErrorType.userDenied) {
          return <FixPermission />
        } else {
          return (
            <ErrorPage
              error={stage.errorType}
              locale={sessionInfo?.sessionSettings.lang || 'en'}
            />
          )
        }
      }
    }
  }

  const renderSession = () => {
    const stageMatches =
      stage.type === StageType.POST_REGISTRATION ||
      stage.type === StageType.REGISTRATION ||
      stage.type === StageType.PERMISSIONS_REQUEST ||
      // stage.type === StageType.PERMISSIONS_ERROR ||
      stage.type === StageType.STARTED
    if (stageMatches && sessionInfo) {
      return (
        <ConnectionContextDataProvider>
          <SessionVideoRecorderDataProvider quality='high'>
            <Session
              started={stage.type === StageType.STARTED}
              sessionInfo={sessionInfo}
              onComplete={onComplete}
              onError={onError}
              onPermissionsError={(permissionError: PermissionErrorType) =>
                setStage({
                  type: StageType.PERMISSIONS_ERROR,
                  errorType: permissionError
                })
              }
              onIdleTimeout={onSessionIdleTimeout}
            />
          </SessionVideoRecorderDataProvider>
        </ConnectionContextDataProvider>
      )
    }
  }

  return (
    <div className='w-full h-full relative flex flex-col justify-center items-center no-scrollbar'>
      {renderSession()}
      {renderContent()}
    </div>
  )
}

export default App
